const productionApiUrl = "https://api.kashtn-auto-n8n.ru";
const localApiUrl = "http://localhost:3002";

export const apiBaseUrl =
  import.meta.env.VITE_API_URL ??
  (typeof window !== "undefined" && window.location.hostname.endsWith("kashtn-auto-n8n.ru")
    ? productionApiUrl
    : localApiUrl);

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly code: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export interface AdminUser {
  id: string;
  username: string;
  createdAt: string;
}

export interface AuthResponse {
  token: string;
  admin: AdminUser;
}

export interface AdminAccount {
  id: string;
  amoAccountId: string;
  plan: "FREE" | "START" | "PRO" | "BUSINESS";
  dialogsUsed: number;
  dialogsResetAt: string;
  createdAt: string;
  user: { id: string; email: string } | null;
  funnelsCount: number;
  dialogsCount: number;
  lastRunAt: string | null;
}

export interface AdminChannel {
  id: string;
  wazzupChannelId: string;
  type: "TELEGRAM" | "WHATSAPP";
}

export interface AdminFunnel {
  id: string;
  accountId: string;
  name: string;
  amoPipelineId: string;
  amoStatusIds: string[];
  systemPrompt: string;
  model: string;
  stopPhrase: string | null;
  escalate: boolean;
  active: boolean;
  createdAt: string;
  updatedAt: string;
  channels: AdminChannel[];
  dialogsCount: number;
  runsCount: number;
  account?: {
    id: string;
    amoAccountId: string;
    user: { id: string; email: string } | null;
  };
}

export interface PipelineRun {
  id: string;
  accountId: string;
  funnelId: string;
  dialogId: string | null;
  chatId: string;
  chatType: string;
  channelId: string;
  wazzupMessageId: string;
  inboundText: string;
  status: "RUNNING" | "DONE" | "SKIPPED" | "FAILED";
  errorStep: string | null;
  startedAt: string;
  finishedAt: string | null;
  createdAt: string;
  eventsCount?: number;
  account?: { id: string; amoAccountId: string };
  funnel?: { id: string; name: string };
}

export interface PipelineEvent {
  id: string;
  runId: string;
  step: string;
  level: "INFO" | "WARN" | "ERROR";
  title: string;
  summary: string;
  details: unknown | null;
  raw: unknown | null;
  createdAt: string;
}

export interface PipelineRunDetail extends PipelineRun {
  account: { id: string; amoAccountId: string };
  funnel: { id: string; name: string };
  events: PipelineEvent[];
}

async function request<T>(
  path: string,
  token: string | null,
  options: RequestInit = {},
): Promise<T> {
  const hasBody = options.body != null;
  const res = await fetch(`${apiBaseUrl}${path}`, {
    ...options,
    headers: {
      ...(hasBody ? { "Content-Type": "application/json" } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    },
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new ApiError(
      (body as Record<string, string>).error ?? res.statusText,
      res.status,
      (body as Record<string, string>).code ?? "UNKNOWN",
    );
  }

  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

export const adminApi = {
  login: (data: { username: string; password: string }) =>
    request<AuthResponse>("/admin/auth/login", null, {
      method: "POST",
      body: JSON.stringify(data),
    }),

  register: (data: { username: string; password: string; inviteKey: string }) =>
    request<AuthResponse>("/admin/auth/register", null, {
      method: "POST",
      body: JSON.stringify(data),
    }),

  me: (token: string) => request<AdminUser>("/admin/auth/me", token),

  accounts: (token: string) => request<AdminAccount[]>("/admin/accounts", token),

  account: (token: string, id: string) =>
    request<AdminAccount>(`/admin/accounts/${id}`, token),

  accountFunnels: (token: string, id: string) =>
    request<AdminFunnel[]>(`/admin/accounts/${id}/funnels`, token),

  funnels: (token: string) =>
    request<AdminFunnel[]>("/admin/funnels", token),

  funnel: (token: string, id: string) =>
    request<AdminFunnel>(`/admin/funnels/${id}`, token),

  funnelRuns: (token: string, id: string) =>
    request<PipelineRun[]>(`/admin/funnels/${id}/runs`, token),

  runs: (token: string) =>
    request<PipelineRun[]>("/admin/runs", token),

  run: (token: string, id: string) =>
    request<PipelineRunDetail>(`/admin/runs/${id}`, token),
};
